import { BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { bootstrapAuthAction, logoutAction } from '../slices/authSlice';
import { recheckSession, refreshIfDue, SessionHooks } from './authSession';
import { exchangeSignupHandoff } from './exchangeSignupHandoff';
import { RootStateType } from '../store';
import i18n from 'src/i18n';
import { toast } from 'react-toastify';
import rateLimit from 'axios-rate-limit';
import { BILLING_SUSPENDED_TOAST_ID, isBillingSuspendedError } from 'src/utils/billingSuspension';
import { EXPORT_OUTDATED } from 'src/utils/exportOutdated';

const http = rateLimit(axios.create(), {
  maxRPS: 10,
});

let baseUrl = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_DEVELOPMENT_BASE_URL : process.env.REACT_APP_PRODUCTION_BASE_URL;

if (!baseUrl || baseUrl === 'auto') {
  baseUrl = window.location.origin;
}

export function getApiOrigin(): string {
  return baseUrl || '';
}

export const createSessionHooks = (getState: () => unknown, dispatch: (action: any) => unknown): SessionHooks => ({
  baseUrl: baseUrl || '',
  getToken: () => (getState() as RootStateType).auth?.token || '',
  adopt: (session) => dispatch(bootstrapAuthAction(session as any)),
  logout: () => dispatch(logoutAction()),
});

export const baseQuery: BaseQueryFn<
  {
    url: string;
    method?: AxiosRequestConfig['method'];
    body?: AxiosRequestConfig['data'];
    params?: AxiosRequestConfig['params'];
    headers?: AxiosRequestConfig['headers'];
    abortController?: AbortController;
    onUploadProgress?: AxiosRequestConfig['onUploadProgress'];
    /** The caller reports every error itself: no global error toast. */
    silentErrors?: boolean;
  },
  unknown,
  unknown
> = async (
  { url, method = 'GET', body, params, headers, abortController, onUploadProgress, silentErrors },
  { getState, dispatch }
) => {
  const hooks = createSessionHooks(getState, dispatch);
  const send = (token: string | undefined) =>
    http({
      url: baseUrl + url,
      method,
      data: body,
      params,
      headers: {
        ...headers,
        ...(token && { authorization: `Bearer ${token}` }),
      },
      ...(abortController && { signal: abortController.signal }),
      onUploadProgress,
    });
  try {
    const sentToken = (getState() as RootStateType).auth?.token;
    let result;
    try {
      result = await send(sentToken);
    } catch (firstError) {
      // One shared re-check per failed token; retry once if the session lives.
      if ((firstError as AxiosError).response?.status !== 401 || !sentToken) {
        throw firstError;
      }
      const check = await recheckSession(sentToken, hooks);
      if (check.kind !== 'retry') throw firstError;
      result = await send(check.token);
    }
    void refreshIfDue(hooks);
    if (method === 'POST' && url === '/api/v1/auth/signup') {
      const session = await exchangeSignupHandoff(result.data, (handoff) =>
        http({
          url: `${baseUrl}/api/v1/auth/handoff`,
          method: 'POST',
          data: { handoff },
        }).then((response) => response.data)
      );
      dispatch(bootstrapAuthAction(session));
    }
    return { data: result.data };
  } catch (axiosError) {
    const e = axiosError as AxiosError;
    const error = {
      status: e.response?.status,
      data: (e.response?.data || e.message) as any,
    };

    if (error?.data === 'canceled' || !error.status) {
      return { error };
    }
    const isTaskProgressRequest = url.includes('/api/v1/tasks/') && url.includes('/progress');
    if (error.status === 502 && isTaskProgressRequest) {
      return { error };
    }
    if (silentErrors) {
      return { error };
    }
    // Enforced SSO: the sign-in form shows its own message and SSO button.
    if (error.status === 403 && error.data?.code === 'SSO_REQUIRED') {
      return { error };
    }
    // Unpaid invoice: one localized message pointing to Manage subscription
    // (the dashboard banner explains it), not the raw backend message.
    if (isBillingSuspendedError(error)) {
      toast.error(i18n.t('billingSuspended.toast'), { toastId: BILLING_SUSPENDED_TOAST_ID });
      return { error };
    }
    // Outdated export (409 EXPORT_OUTDATED): the caller shows a translated
    // "anonymize again" message instead of the English server text.
    if (error.status === 409 && error.data?.code === EXPORT_OUTDATED) {
      return { error };
    }
    switch (error.status) {
      case 404:
        return { error };
      case 401:
        // The re-check above already decided about the session: a
        // still-401 request after a successful retry is not a logout.
        break;
      default:
        toast.error(error.data?.message ? `${error.data.message} (status code: ${error.status})` : i18n.t('somethingWentWrongTryAgain'));
        break;
    }
    return { error };
  }
};
